export interface Attraction {
  name: string;
  emoji: string;
  // Extra words search should find this place by (activity, occasion).
  tags?: string[];
  description: string;
  address: string;
  website: string;
  imageClass: string;
  backgroundImage: string;
  proTip?: string;
  neighborhood?: string;
  coordinates?: [number, number];
}

export interface AttractionCategory {
  title: string;
  emoji: string;
  attractions: Attraction[];
}

export const attractionCategories: AttractionCategory[] = [
  {
    title: "Capitol Hill Hit List",
    emoji: "🏡",
    attractions: [
      {
        name: "Chophouse Row",
        tags: ['shopping', 'shops', 'rainy day', 'courtyard', 'cafes'],
        emoji: "🏛️",
        description: "A favorite for a rainy afternoon. This converted auto-shop building on 11th Ave hides a courtyard of small local shops, cafés, and bars — about as Capitol Hill as it gets.",
        address: "1424 11th Ave, Seattle, WA 98122",
        website: "https://www.chophouserow.com",
        imageClass: "chophouse-row",
        backgroundImage: "images/attractions-chophouse-row.webp",
        neighborhood: "capitol-hill",
        coordinates: [-122.3199, 47.6141]
      },
      {
        name: "Elliott Bay Book Company",
        tags: ['books', 'bookstore', 'rainy day', 'shopping', 'cafe'],
        emoji: "📚",
        description: "We send every book lover here. Seattle's best-loved independent bookstore has creaky wood floors, tall cedar shelves, a big local-authors section, and a café for reading over coffee.",
        address: "1521 10th Ave, Seattle, WA 98122",
        website: "https://www.elliottbaybook.com",
        imageClass: "elliott-bay",
        backgroundImage: "images/attractions-elliott-bay-new.webp",
        proTip: "Check their event calendar for frequent author talks and readings.",
        neighborhood: "capitol-hill",
        coordinates: [-122.3196770, 47.6146657]
      },
      {
        name: "Cal Anderson Park",
        tags: ['park', 'picnic', 'free', 'kid-friendly', 'outdoors'],
        emoji: "⚽",
        description: "The neighborhood's backyard. On a sunny day all of Capitol Hill turns up here to picnic, play pickup games, and people-watch around the fountain.",
        address: "1635 11th Ave, Seattle, WA 98122",
        website: "https://www.seattle.gov/parks/allparks/cal-anderson-park",
        imageClass: "cal-anderson",
        backgroundImage: "images/attractions-cal-anderson-new.webp",
        proTip: "Grab a scoop from nearby Molly Moon's ice cream, a Seattle classic.",
        neighborhood: "capitol-hill",
        coordinates: [-122.3182882, 47.6156969]
      },
      {
        name: "Volunteer Park",
        tags: ['park', 'conservatory', 'water tower', 'views', 'free', 'walk', 'kid-friendly'],
        emoji: "🌳",
        description: "Our favorite spot for a slow morning walk. Climb the old water tower for a free view, wander the Victorian conservatory, and find where Isamu Noguchi's Black Sun sculpture frames the skyline. The Seattle Asian Art Museum is right in the park, and Bruce and Brandon Lee rest in the cemetery next door.",
        address: "1247 15th Ave E, Seattle, WA 98112",
        website: "https://www.seattle.gov/parks/allparks/volunteer-park",
        imageClass: "volunteer-park",
        backgroundImage: "images/attractions-arboretum-sculpture.webp",
        proTip: "Walk there via Federal Ave E to see the beautiful historic homes.",
        neighborhood: "capitol-hill",
        coordinates: [-122.3126472, 47.6309901]
      },
      {
        name: "Washington Park Arboretum",
        tags: ['park', 'garden', 'hiking', 'walk', 'nature', 'japanese garden', 'free'],
        emoji: "🌺",
        description: "Our pick when you want to feel far from the city without leaving it: 230 acres of trails, with Azalea Way blooming in spring and blazing maples in fall. The Japanese Garden inside is one of the most peaceful places in Seattle.",
        address: "2300 Arboretum Dr E, Seattle, WA 98112",
        website: "https://botanicgardens.uw.edu/washington-park-arboretum",
        imageClass: "washington-arboretum",
        backgroundImage: "images/attractions-arboretum-japanese-garden.webp",
        proTip: "The Japanese Garden has an entry fee, so check ahead.",
        neighborhood: "capitol-hill",
        coordinates: [-122.2943472, 47.6399346]
      },
      {
        name: "Madison Park Beach",
        tags: ['beach', 'swimming', 'lake', 'summer', 'picnic', 'kid-friendly'],
        emoji: "🏖️",
        description: "Where we head on a hot summer day. A sandy little beach on Lake Washington with lifeguards in summer, a grassy slope for picnics, and mountain views — then ice cream in the village afterward.",
        address: "1900 43rd Ave E, Seattle, WA 98112",
        website: "https://www.seattle.gov/parks/allparks/madison-park",
        imageClass: "madison-park",
        backgroundImage: "images/attractions-madison-park-new.webp",
        neighborhood: "capitol-hill",
        coordinates: [-122.2768031, 47.6360866]
      }
    ]
  },
  {
    title: "Space Needle & Seattle Center",
    emoji: "🗼",
    attractions: [
      {
        name: "MoPOP (Museum of Pop Culture)",
        tags: ['museum', 'music', 'rainy day', 'kid-friendly', 'jimi hendrix'],
        emoji: "🎸",
        description: "Worth it for the swooping Frank Gehry building alone. Inside, it's a fun romp through music, sci-fi, horror, and video games, with deep dives on Seattle's own Jimi Hendrix and Nirvana.",
        address: "325 5th Ave N, Seattle, WA 98109",
        website: "https://www.mopop.org",
        imageClass: "mopop",
        backgroundImage: "images/attractions-mopop.webp",
        neighborhood: "queen-anne",
        coordinates: [-122.3482253, 47.6214931]
      },
      {
        name: "Space Needle / Seattle Center",
        tags: ['views', 'observation deck', 'landmark', 'kid-friendly'],
        emoji: "🗼",
        description: "You have to do it once. A 43-second elevator ride takes you up the 605-foot tower for a 360° view of downtown, Elliott Bay, and Mount Rainier on clear days — then stand on the rotating glass floor if you dare.",
        address: "Seattle Center, Seattle, WA 98109",
        website: "https://www.spaceneedle.com",
        imageClass: "space-needle",
        backgroundImage: "images/attractions-space-needle.webp",
        neighborhood: "queen-anne",
        coordinates: [-122.3493036, 47.6205131]
      },
      {
        name: "Chihuly Garden & Glass",
        tags: ['museum', 'art', 'glass', 'garden', 'rainy day'],
        emoji: "🎨",
        description: "Even people who don't do museums love this one. Tacoma-born glass artist Dale Chihuly fills the galleries and garden with huge, colorful glass pieces — beautiful by day and even better lit up at night.",
        address: "305 Harrison St, Seattle, WA 98109",
        website: "https://www.chihulygardenandglass.com",
        imageClass: "chihuly",
        backgroundImage: "images/attractions-chihuly-garden.webp",
        neighborhood: "queen-anne",
        coordinates: [-122.3509886, 47.6214620]
      },
      {
        name: "Kerry Park",
        tags: ['views', 'sunset', 'photo', 'free', 'skyline'],
        emoji: "📸",
        description: "The classic postcard shot of Seattle: the Space Needle, downtown, Elliott Bay, and Mount Rainier on a clear day, all from one small hilltop park in Queen Anne. Go at sunset.",
        address: "211 W Highland Dr, Seattle, WA 98119",
        website: "https://www.seattle.gov/parks/allparks/kerry-park",
        imageClass: "kerry-park",
        backgroundImage: "images/attractions-kerry-park.webp",
        neighborhood: "queen-anne",
        coordinates: [-122.3599350, 47.6294772]
      }
    ]
  },
  {
    title: "Fremont & Ballard",
    emoji: "🛶",
    attractions: [
      {
        name: "Gas Works Park",
        tags: ['park', 'picnic', 'views', 'skyline', 'kite', 'free', 'sunset'],
        emoji: "🏭",
        description: "Our favorite picnic spot. The rusting towers of an old gas plant sit beside a grassy hill with one of the best views of Lake Union and the skyline. Bring a kite and watch the seaplanes come in.",
        address: "2101 N Northlake Way, Seattle, WA 98103",
        website: "https://www.seattle.gov/parks/allparks/gas-works-park",
        imageClass: "gas-works",
        backgroundImage: "images/attractions-lake-union-kayak.webp",
        neighborhood: "fremont",
        coordinates: [-122.3327490, 47.6475500]
      },
      {
        name: "Fremont Troll",
        tags: ['sculpture', 'photo', 'free', 'quirky', 'kid-friendly'],
        emoji: "🧌",
        description: "Fremont's quirkiest resident: an 18-foot concrete troll crushing a real VW Beetle under the Aurora Bridge. Climb up for the photo — everyone does.",
        address: "820 N 36th St, Seattle, WA 98103",
        website: "https://fremont.com/explore/fremonttroll/",
        imageClass: "fremont-troll",
        backgroundImage: "images/attractions-fremont-troll.webp",
        neighborhood: "fremont",
        coordinates: [-122.3478054, 47.6509438]
      },
      {
        name: "Ballard Sunday Farmers Market",
        tags: ['farmers market', 'market', 'food', 'sunday', 'shopping'],
        emoji: "🥬",
        description: "Our favorite way to spend a Sunday. Every week, rain or shine, Washington farmers, bakers, and makers fill historic Ballard Avenue — come hungry, then browse the shops along the street.",
        address: "Ballard Ave NW, Seattle, WA 98107",
        website: "https://www.sfmamarkets.com/visit-ballard-farmers-market",
        imageClass: "ballard-market",
        backgroundImage: "images/attractions-ballard-farmers-market.webp",
        neighborhood: "ballard",
        coordinates: [-122.3830502, 47.6666297]
      },
      {
        name: "Ballard Locks & Fish Ladder",
        tags: ['salmon', 'boats', 'free', 'kid-friendly', 'garden'],
        emoji: "🐟",
        description: "Free and genuinely fascinating. Watch boats get raised and lowered between the lakes and Puget Sound, then head down to the fish ladder windows to see salmon swimming upstream (best in summer). The botanical garden next door is lovely too.",
        address: "3015 NW 54th St, Seattle, WA 98107",
        website: "https://ballardlocks.org/fish-salmon-ladder.html",
        imageClass: "ballard-locks",
        backgroundImage: "images/attractions-ballard-locks.webp",
        neighborhood: "ballard",
        coordinates: [-122.3974596, 47.6646617]
      },
      {
        name: "Point Shilshole Beach",
        tags: ['beach', 'sunset', 'views', 'quiet', 'free'],
        emoji: "🌊",
        description: "When we want a quiet beach. This low-key stretch of Puget Sound shoreline near the marina has big views of the Olympic Mountains and great sunsets, without the Golden Gardens crowds.",
        address: "6321 Seaview Ave NW, Seattle, WA 98107",
        website: "https://www.seattle.gov/parks/allparks/golden-gardens-park",
        imageClass: "shilshole-beach",
        backgroundImage: "images/attractions-alki-beach.webp",
        neighborhood: "ballard",
        coordinates: [-122.4081937, 47.6744674]
      }
    ]
  },
  {
    title: "University District",
    emoji: "🎓",
    attractions: [
      {
        name: "University of Washington Campus",
        tags: ['campus', 'cherry blossoms', 'library', 'walk', 'free'],
        emoji: "🌸",
        description: "One of the prettiest campuses in the country, and free to explore. Wander the Gothic-style buildings around Red Square, peek into Suzzallo Library's grand reading room, and catch the famous cherry blossoms on the Quad in spring.",
        address: "University of Washington, Seattle, WA 98195",
        website: "https://www.washington.edu/visit/",
        imageClass: "uw-campus",
        backgroundImage: "images/attractions-uw-cherry-blossoms.webp",
        proTip: "Visit in late March for the cherry blossom bloom on the Quad.",
        neighborhood: "university-district",
        coordinates: [-122.3035, 47.6553]
      },
      {
        name: "Burke Museum",
        tags: ['museum', 'dinosaurs', 'fossils', 'kid-friendly', 'rainy day'],
        emoji: "🦖",
        description: "Great for curious kids and adults alike. Washington's natural history and culture museum shows real dinosaur fossils and Pacific Northwest Native art, and its glass-walled labs let you watch scientists at work.",
        address: "4300 15th Ave NE, Seattle, WA 98105",
        website: "https://www.burkemuseum.org",
        imageClass: "burke-museum",
        backgroundImage: "images/attractions-burke-museum.webp",
        proTip: "Free admission first Thursday of every month.",
        neighborhood: "university-district",
        coordinates: [-122.3108, 47.6563]
      },
      {
        name: "University Village",
        tags: ['shopping', 'shops', 'stores', 'parking'],
        emoji: "🛍️",
        description: "Our pick for an easy shopping afternoon: an open-air village of favorite stores and good places to eat, with plenty of parking.",
        address: "2623 NE University Village St, Seattle, WA 98105",
        website: "https://www.uvillage.com",
        imageClass: "u-village",
        backgroundImage: "images/attractions-u-village.webp",
        neighborhood: "university-district",
        coordinates: [-122.2976, 47.6631]
      }
    ]
  },
  {
    title: "Pike Place Market / Waterfront",
    emoji: "🎣",
    attractions: [
      {
        name: "Seattle Underground Tour",
        tags: ['tour', 'history', 'pioneer square', 'rainy day', 'quirky'],
        emoji: "⛏️",
        description: "Seattle's quirkiest history lesson. Guides lead you through the buried storefronts and sidewalks beneath Pioneer Square, with plenty of laughs about the city's messy past.",
        address: "614 1st Ave, Seattle, WA 98104",
        website: "https://www.undergroundtour.com",
        imageClass: "underground-tour",
        backgroundImage: "images/attractions-underground-tour.webp",
        neighborhood: "downtown",
        coordinates: [-122.3338244, 47.6023240]
      },
      {
        name: "Seattle Art Museum",
        tags: ['museum', 'art', 'rainy day', 'sculpture park'],
        emoji: "🖼️",
        description: "Seattle's main art museum, right downtown — look for the 48-foot Hammering Man out front. Exhibits change often, and the free Olympic Sculpture Park on the waterfront makes a lovely add-on.",
        address: "1300 1st Ave, Seattle, WA 98101",
        website: "https://www.seattleartmuseum.org",
        imageClass: "seattle-art-museum",
        backgroundImage: "images/events-february.webp",
        neighborhood: "downtown",
        coordinates: [-122.3385575, 47.6077008]
      },
      {
        name: "Pike Place Market",
        tags: ['market', 'fish', 'gum wall', 'starbucks', 'shopping', 'food'],
        emoji: "🐟",
        description: "A must on your first visit. Watch the fishmongers toss salmon, browse 225+ local artisans, snack your way through the stalls, and see the gum wall and the original Starbucks. The Overlook Walk takes you straight down to the waterfront.",
        address: "Pike Place, Seattle, WA 98101",
        website: "https://www.pikeplacemarket.org",
        imageClass: "pike-place",
        backgroundImage: "images/dining-pike-place-waterfront.webp",
        neighborhood: "downtown",
        coordinates: [-122.3401999, 47.6087536]
      },
      {
        name: "Seattle Aquarium",
        tags: ['aquarium', 'sea otters', 'sharks', 'kid-friendly', 'rainy day'],
        emoji: "🐠",
        description: "Perfect for families and rainy days. Meet the sea otters and Puget Sound sea life, then visit the Ocean Pavilion, opened in 2024, with its coral reef and sharks.",
        address: "1483 Alaskan Way, Seattle, WA 98101",
        website: "https://www.seattleaquarium.org",
        imageClass: "seattle-aquarium",
        backgroundImage: "images/attractions-seattle-aquarium.webp",
        neighborhood: "downtown",
        coordinates: [-122.3432202, 47.6076247]
      }
    ]
  },
  {
    title: "Lake Union",
    emoji: "🌊",
    attractions: [
      {
        name: "Moss Bay Rentals",
        tags: ['kayak', 'paddleboard', 'boats', 'houseboats', 'outdoors'],
        emoji: "🛶",
        description: "Our favorite way to see Lake Union up close. Rent a kayak or paddleboard and glide past the houseboats — including the one from Sleepless in Seattle — with the skyline behind you.",
        address: "1001 Fairview Ave N, Seattle, WA 98109",
        website: "https://www.mossbay.co",
        imageClass: "moss-bay",
        backgroundImage: "images/attractions-lake-union-kayak.webp",
        neighborhood: "lake-union",
        coordinates: [-122.3317974, 47.6291170]
      },
      {
        name: "Lake Union Park",
        tags: ['park', 'seaplanes', 'museum', 'mohai', 'boats', 'free', 'picnic'],
        emoji: "🚤",
        description: "An easy, free stop in South Lake Union. Watch seaplanes land, visit MOHAI (Seattle's history museum) and the Center for Wooden Boats, or just picnic by the water.",
        address: "860 Terry Ave N, Seattle, WA 98109",
        website: "https://www.seattle.gov/parks/allparks/lake-union-park",
        imageClass: "lake-union-park",
        backgroundImage: "images/attractions-lake-union.webp",
        neighborhood: "lake-union",
        coordinates: [-122.3367506, 47.6275114]
      },
      {
        name: "Kenmore Air Scenic Flights",
        tags: ['seaplane', 'flight', 'tour', 'views', 'special occasion'],
        emoji: "✈️",
        description: "The most memorable way to see Seattle. A short seaplane flight takes off right from Lake Union and loops over downtown, the Space Needle, and the water — unforgettable on a clear day. Check their site for current flights and prices.",
        address: "950 Westlake Ave N, Seattle, WA 98109",
        website: "https://www.kenmoreair.com",
        imageClass: "kenmore-air-seaplane",
        backgroundImage: "images/attractions-kenmore-air-seaplane.webp",
        neighborhood: "lake-union",
        coordinates: [-122.3391850, 47.6283095]
      }
    ]
  }
];
